import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DownloadIconComponent } from '../../icons/download-icon/download-icon.component';
import { RestoreIconComponent } from '../../icons/restore-icon/restore-icon.component';
import { RouterModule } from '@angular/router';
import { SwPartNumberService } from '../../../services/sw-part-number.service';
import { DtcQuery } from '../../../../shared/models/dtc/dtc-query';
import { CommonModule } from '@angular/common';
import { IfPolicySatisfiedDirective } from '../../../directives/if-policy-satisfied.directive';
import { IfPolicyNotSatisfiedDirective } from '../../../directives/if-policy-not-satisfied.directive';
import { Policy } from '../../../../shared/models/info/policy';
import { PreselectionService } from '../../../services/preselection.service';
import { SearchIconComponent } from '../../icons/search-icon/search-icon.component';
import { PreselectionType } from '../../../../shared/enums/preselection-type';
import { SwPartNumberState } from '../../../../shared/enums/sw-part-number-state';
import { Subscription } from 'rxjs';
import { DtcFilterComponent } from '../dtc/dtc-filter/dtc-filter.component';
import { FilterIconComponent } from '../../icons/filter-icon/filter-icon.component';
import { HelpPopoverComponent } from '../../common/popover/help-popover/help-popover.component';
import { initFlowbite } from 'flowbite';
import { Paragraph } from '../../../../shared/models/internal/popover/paragraph';
import { DomSanitizer } from '@angular/platform-browser';
import { buildTranslationStateColorsHelp } from '../../../../shared/internal/build-translation-state-colors-help';
import { LanguageSelectionComponent } from '../../common/language/language-selection/language-selection.component';
import { SwPartNumberSelectionComponent } from '../../common/sw-part-number/sw-part-number-selection/sw-part-number-selection.component';
import { AgentActionDirective } from '../../../directives/agent-action.directive';

@Component({
  selector: 'app-dtc-table-search',
  imports: [
    FormsModule,
    DownloadIconComponent,
    RestoreIconComponent,
    RouterModule,
    CommonModule,
    IfPolicySatisfiedDirective,
    IfPolicyNotSatisfiedDirective,
    SearchIconComponent,
    DtcFilterComponent,
    FilterIconComponent,
    HelpPopoverComponent,
    LanguageSelectionComponent,
    SwPartNumberSelectionComponent,
    AgentActionDirective,
  ],
  templateUrl: './dtc-table-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dtc-table-search.component.scss',
})
export class DtcTableSearchComponent implements OnInit, OnDestroy {
  @Input() public recyclingModeEnabled = false;
  @Input() public downloadDisabled = false;
  @Input() public restoreDisabled = false;
  @Input() public helpPopoverEnabled = true;
  @Input() public displaySwPartNumberSelection = true;
  @Input() public languageSelection = true;
  @Output() public searchTriggered: EventEmitter<DtcQuery> = new EventEmitter<DtcQuery>();
  @Output() public downloadTriggered: EventEmitter<void> = new EventEmitter<void>();

  public searchQuery: DtcQuery = {
    searchPattern: '',
    searchInOtherFields: true,
    searchInValue: true,
  } as DtcQuery;
  public policy = Policy;
  public csvExportEnabled = false;
  public filterVisible = false;
  public paragraphs: Paragraph[] = [];

  private swPartNumberService = inject(SwPartNumberService);
  private preselectionService = inject(PreselectionService);
  private readonly sanitizer = inject(DomSanitizer);
  private swPartNumberChangeSubjectSubscription: Subscription | undefined;

  public ngOnInit(): void {
    this.paragraphs = buildTranslationStateColorsHelp(this.sanitizer);
    initFlowbite();
    this.searchQuery.searchPattern = this.preselectionService.getSearchTermAndClear(PreselectionType.Dtc);

    this.csvExportEnabled = this.swPartNumberService.getSwPartNumber()?.state != SwPartNumberState.Dev;

    this.swPartNumberChangeSubjectSubscription = this.swPartNumberService.swPartNumberChangeSubject.subscribe({
      next: () => {
        this.csvExportEnabled = this.swPartNumberService.getSwPartNumber()?.state != SwPartNumberState.Dev;
        this.search();
      },
    });
  }

  public ngOnDestroy(): void {
    if (this.swPartNumberChangeSubjectSubscription) {
      this.swPartNumberChangeSubjectSubscription.unsubscribe();
    }
  }

  public toggleFilter(): void {
    this.filterVisible = !this.filterVisible;
  }

  public searchByKey(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.search();
    } else if (event.key === 'Escape') {
      this.clearSearch();
    }
  }

  public search(): void {
    this.searchTriggered.emit(this.searchQuery);
  }

  public download(): void {
    this.downloadTriggered.emit();
  }

  public filterApplied(query: DtcQuery): void {
    query.searchPattern = this.searchQuery.searchPattern;
    query.page = this.searchQuery.page;
    query.pageSize = this.searchQuery.pageSize;
    query.orderBy = this.searchQuery.orderBy;
    query.orderByDescending = this.searchQuery.orderByDescending;
    this.searchQuery = { ...query };
    this.searchTriggered.emit(this.searchQuery);
  }

  public filtersAreSet(): boolean {
    const defaultFilter: DtcQuery = {
      searchInOtherFields: true,
      searchInValue: true,
    };
    return (
      this.searchQuery.searchInOtherFields !== defaultFilter.searchInOtherFields ||
      this.searchQuery.searchInValue !== defaultFilter.searchInValue ||
      this.searchQuery.detailsOnTerminalNotEmpty !== undefined ||
      this.searchQuery.causeInternNotEmpty !== undefined ||
      this.searchQuery.causeDealerNotEmpty !== undefined ||
      this.searchQuery.causeFarmerNotEmpty !== undefined ||
      this.searchQuery.implementationHintNotEmpty !== undefined ||
      this.searchQuery.remedyInternNotEmpty !== undefined ||
      this.searchQuery.remedyDealerNotEmpty !== undefined ||
      this.searchQuery.remedyFarmerNotEmpty !== undefined ||
      (this.searchQuery.translationStatesTerminal?.length ?? 0) > 0 ||
      (this.searchQuery.translationStatesDocumentation?.length ?? 0) > 0 ||
      this.searchQuery.hasErrorMessage !== undefined ||
      this.searchQuery.mainLanguageTerminalFieldsExportedForReview === true ||
      this.searchQuery.mainLanguageTerminalFieldsReviewed !== undefined ||
      this.searchQuery.mainLanguageDocumentationFieldsExportedForReview === true ||
      this.searchQuery.mainLanguageDocumentationFieldsReviewed !== undefined ||
      this.searchQuery.isReviewedByAfterSales !== undefined ||
      this.searchQuery.hasActiveAfterSalesReview !== undefined ||
      this.searchQuery.isReviewedByTechnicalOrganisation !== undefined ||
      this.searchQuery.hasActiveTechnicalOrganisationReview !== undefined
    );
  }

  private clearSearch(): void {
    this.searchQuery.searchPattern = '';
    this.searchTriggered.emit(this.searchQuery);
  }
}
